var RevistaSearch = RevistaSearch || {};

// ============================================================================
// GERENCIADOR DE ESTADO DE UI
// ============================================================================

RevistaSearch.UIState = {
  setPaginationVisible(visible) {
    const { dom } = RevistaSearch;
    if (!dom.paginationRoot) return;
    dom.paginationRoot.style.display = visible ? '' : 'none';
  },

  setLoading(isLoading) {
    RevistaSearch.state.isLoading = isLoading;
    if (RevistaSearch.dom.resultsContainer) {
      RevistaSearch.dom.resultsContainer.setAttribute('aria-busy', String(isLoading));
    }
  },

  showSkeleton(size) {
    const { CONFIG, dom, Templates } = RevistaSearch;
    const raw = size || CONFIG.PAGINATION.DEFAULT_SIZE;
    const count = Math.min(
      Math.max(raw, CONFIG.PAGINATION.MIN_SKELETON_ITEMS),
      CONFIG.PAGINATION.MAX_SKELETON_ITEMS
    );
    dom.resultsList.classList.add('search-results__list--skeleton');
    dom.resultsList.innerHTML = Templates.skeleton().repeat(count);
  },

  hideSkeleton() {
    const { dom, Formatters } = RevistaSearch;
    dom.resultsList.classList.remove('search-results__list--skeleton');
    Formatters.clearElement(dom.resultsList);
  },

  updateResultsInfo(page, size, totalCount, itemCount, query) {
    const { dom, Formatters } = RevistaSearch;
    if (!dom.resultsInfo) return;

    const safeQuery = Formatters.escapeHtml(query || '');

    if (itemCount === 0) {
      if (dom.resultsHeader) dom.resultsHeader.style.display = 'none';
      Formatters.clearElement(dom.resultsInfo);
      return;
    }

    if (dom.resultsHeader) dom.resultsHeader.style.display = '';

    const start = page * size + 1;
    const end = page * size + itemCount;
    const total = totalCount != null ? totalCount : end;

    dom.resultsInfo.innerHTML = `
        Mostrando os resultados <strong>${start}-${end}</strong>
        de <strong>${total}</strong> para
        <strong>"${safeQuery}"</strong>`;
  },

  resetResultsInfo() {
    const { dom, Formatters } = RevistaSearch;
    if (dom.resultsHeader) dom.resultsHeader.style.display = '';
    if (dom.resultsInfo) Formatters.clearElement(dom.resultsInfo);
  },
};

// ============================================================================
// GERENCIADOR DE FORMULÁRIO
// ============================================================================

RevistaSearch.FormManager = {
  getFormData() {
    const { dom, Formatters } = RevistaSearch;
    return {
      query: Formatters.sanitizeString(dom.queryInput?.value),
      match: this.getSelectedMatch(),
      sort: dom.sortSelect?.value || 'relevance',
      dateFrom: Formatters.sanitizeDate(dom.dateFromInput?.value),
      dateTo: Formatters.sanitizeDate(dom.dateToInput?.value),
      types: this.getSelectedTypes(),
    };
  },

  getSelectedMatch() {
    const selected = RevistaSearch.dom.matchRadios.find(radio => radio.checked);
    return selected?.value || 'all';
  },

  getSelectedTypes() {
    const { dom, Mappers, ALL_TYPES_VALUE } = RevistaSearch;
    const checked = dom.typeCheckboxes.filter(cb => cb.checked);

    if (checked.some(cb => cb.value === 'all')) return ALL_TYPES_VALUE;
    if (checked.length === 0) return null;

    const types = checked.map(cb => Mappers.formTypeToApi(cb.value)).filter(Boolean);
    return types.length ? types.join(',') : null;
  },

  setFormData(data) {
    const { dom } = RevistaSearch;

    if (data.query && dom.queryInput) dom.queryInput.value = data.query;

    if (data.match) {
      dom.matchRadios.forEach(radio => { radio.checked = radio.value === data.match; });
    }

    if (data.sort && dom.sortSelect) dom.sortSelect.value = data.sort;
    if (data.from && dom.dateFromInput) dom.dateFromInput.value = data.from;
    if (data.to && dom.dateToInput) dom.dateToInput.value = data.to;
    if (data.types) this.setTypeCheckboxes(data.types);
  },

  setTypeCheckboxes(typesStr) {
    const { dom, Mappers, ALL_TYPES_VALUE } = RevistaSearch;
    const allCheckbox = dom.typeCheckboxes.find(cb => cb.value === 'all');

    if (typesStr === 'all' || typesStr === ALL_TYPES_VALUE) {
      if (allCheckbox) {
        allCheckbox.checked = true;
        dom.typeCheckboxes.filter(cb => cb.value !== 'all').forEach(cb => cb.checked = false);
      }
      return;
    }

    const formTypes = typesStr.split(',')
      .map(t => Mappers.apiTypeToForm(t.trim()))
      .filter(Boolean);

    if (allCheckbox) allCheckbox.checked = false;
    dom.typeCheckboxes.forEach(cb => {
      if (cb.value !== 'all') cb.checked = formTypes.includes(cb.value);
    });
  },

  setupDateRangeLimits() {
    const { dom } = RevistaSearch;
    if (!dom.dateFromInput || !dom.dateToInput) return;

    const syncFromToMin = () => { dom.dateToInput.min = dom.dateFromInput.value || ''; };
    const syncToFromMax = () => { dom.dateFromInput.max = dom.dateToInput.value || ''; };

    dom.dateFromInput.addEventListener('change', syncFromToMin);
    dom.dateToInput.addEventListener('change', syncToFromMax);

    syncFromToMin();
    syncToFromMax();
  },

  setupTypeCheckboxes() {
    const { dom } = RevistaSearch;
    const allCheckbox = dom.typeCheckboxes.find(cb => cb.value === 'all');
    const others = dom.typeCheckboxes.filter(cb => cb.value !== 'all');

    if (!allCheckbox || !others.length) return;

    allCheckbox.addEventListener('change', () => {
      if (allCheckbox.checked) others.forEach(cb => cb.checked = false);
    });

    others.forEach(cb => {
      cb.addEventListener('change', () => {
        if (cb.checked) allCheckbox.checked = false;
      });
    });
  },
};

// ============================================================================
// RENDERIZADOR DE RESULTADOS
// ============================================================================

RevistaSearch.ResultsRenderer = {
  render(items, metadata) {
    const { dom, UIState } = RevistaSearch;
    UIState.hideSkeleton();

    if (!items || items.length === 0) {
      this.renderEmptyState(metadata.query);
      UIState.updateResultsInfo(0, metadata.size, 0, 0, metadata.query);
      return;
    }

    dom.resultsList.innerHTML = items.map(item => this.getItemHTML(item)).join('');

    UIState.updateResultsInfo(
      metadata.page,
      metadata.size,
      metadata.total,
      items.length,
      metadata.query
    );
  },

  renderInitialState() {
    const { dom, CONFIG, Templates } = RevistaSearch;
    if (!dom || !dom.resultsList) return;
    dom.resultsList.innerHTML = Templates.initialState(CONFIG.ASSETS.warningBlueIcon);
  },

  renderEmptyState(query) {
    const { dom, Formatters, CONFIG, Templates } = RevistaSearch;
    const safeQuery = Formatters.escapeHtml(query || '');
    dom.resultsList.innerHTML = Templates.emptyState(safeQuery, CONFIG.ASSETS.emptySearchIcon);
  },

  renderErrorState(error) {
    const { dom, Formatters, CONFIG, UIState, Templates } = RevistaSearch;
    UIState.hideSkeleton();
    UIState.resetResultsInfo();
    UIState.setPaginationVisible(false);

    const safeMessage = Formatters.escapeHtml('Problema ao se conectar com o servidor, tente novamente mais tarde.');
    dom.resultsList.innerHTML = Templates.errorState(safeMessage, CONFIG.ASSETS.errorIcon);
  },

  getItemHTML(item) {
    const { CONTENT_TYPES, CONFIG, Formatters, Templates } = RevistaSearch;
    const tipo = item?.tipo || '';
    const conteudo = item?.conteudo || {};
    const highlights = Array.isArray(item?.highlights) ? item.highlights : [];

    const typeConfig = CONTENT_TYPES[tipo] || {
      display: tipo || 'Documento',
      showBadge: true,
      showAuthor: false,
      defaultTitle: null,
    };

    const title = typeConfig.defaultTitle
      ? typeConfig.defaultTitle
      : Formatters.escapeHtml(conteudo.titulo || conteudo.revista || 'Documento da revista');

    const documentUrl = Formatters.escapeHtml(conteudo.document || '#');
    const revistaUrl = this.getRevistaCompletaUrl(conteudo, documentUrl);

    return Templates.resultItem({
      pdfIcon: CONFIG.ASSETS.pdfIcon,
      typeBadge: typeConfig.showBadge ? Templates.typeBadge(Formatters.escapeHtml(typeConfig.display)) : '',
      title,
      description: this.getDescription(conteudo, highlights),
      meta: Templates.meta(typeConfig, Formatters.escapeHtml(conteudo.revista || '-'), Formatters.escapeHtml(conteudo.autor || conteudo.Autor || '-')),
      cta: Templates.cta(documentUrl, revistaUrl, `Ver ${typeConfig.display.toLowerCase()}`),
    });
  },

  getDescription(conteudo, highlights) {
    const { Formatters } = RevistaSearch;
    if (highlights.length > 0) return highlights[0];
    if (conteudo.informacoesComplementares) return Formatters.escapeHtml(conteudo.informacoesComplementares);
    if (conteudo.autor) return Formatters.escapeHtml(`Autor: ${conteudo.autor}`);
    if (conteudo.categorias) return Formatters.escapeHtml(conteudo.categorias);
    return '';
  },

  getRevistaCompletaUrl(conteudo, documentUrl) {
    if (conteudo.contentId) {
      return `/contenthyperlink.jsp?lumItemId=${RevistaSearch.Formatters.escapeHtml(String(conteudo.contentId))}`;
    }
    return documentUrl;
  },
};
